import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { styled } from '@mui/material/styles';
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ChatBubbleOutlineOutlined as ChatBubbleOutlineOutlinedIcon,
  ModeOfTravelOutlined as ModeOfTravelOutlinedIcon,
  ArrowForwardIosOutlined as ArrowForwardIosOutlinedIcon,
  CheckCircleOutlineRounded as CheckCircleOutlineRoundedIcon,
} from '@mui/icons-material';

import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useFeatureReviewInvoicing } from '../../../hooks/features/useFeatureReviewInvoicing';

import { TripStatusTodaysTripsBCList } from '../../../data/models/trip-leg-status.model';
import { TripLegEventType } from '../../../services/webSocketService/updateTripLeg';

import * as triplegSlice from '../../../data/tripleg.slice';
import * as accountingSlice from '../../../data/accounting.slice';

import dateUtils from '../../../services/DateUtils';

const PREFIX = 'PR2HomePage';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  content: `${PREFIX}-content`,
  section: `${PREFIX}-section`,
  icon: `${PREFIX}-icon`,
  info: `${PREFIX}-info`,
  nav: `${PREFIX}-nav`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    width: '668px',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(3),
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
  },
  [`& .${classes.section}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    backgroundColor: '#0110120A',
    padding: theme.spacing(3),
    borderRadius: '8px',
    alignItems: 'center',
  },
  [`& .${classes.icon}`]: {
    color: '#0110128A',
  },
  [`& .${classes.info}`]: {
    marginLeft: theme.spacing(3),
  },
  [`& .${classes.nav}`]: {
    marginLeft: 'auto',
  },
}));

export default function PR2HomePage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const enableReviewInvoicing = useFeatureReviewInvoicing();

  const todaysLegs = useAppSelector(triplegSlice.selectTodaysTripLegs);
  const loadingTodaysTrips = useAppSelector(
    triplegSlice.selectLoadingTodaysTripLegs,
  );
  const discussionLegs = useAppSelector(triplegSlice.selectDiscussionTripsLegs);
  const loadingDiscussionTrips = useAppSelector(
    triplegSlice.selectLoadingDiscussionTripLegs,
  );
  const tripLegEventEmitted = useAppSelector(
    triplegSlice.selectTripLegEventEmitted,
  );
  const needsReviewData = useAppSelector(accountingSlice.selectNeedsReviewData);
  const loadingNeedsReview = useAppSelector(
    accountingSlice.selectLoadingNeedsReview,
  );

  const fetchTripLegs = () => {
    const today = DateTime.now().toISODate();
    dispatch(
      triplegSlice.fetchTodaysTripLegsAsync({
        page: 1,
        pageSize: 1,
        sortModel: {
          column: 'pickUpDueTimeUTC',
          direction: 'asc',
        },
        tripLegStatusBCs: [...TripStatusTodaysTripsBCList],
        includeFindProviderCallOutcomes: false,
        appointmentStartDate: today,
        appointmentEndDate: today,
        isEscalated: true,
      }),
    );
    dispatch(
      triplegSlice.fetchDiscussionTripLegsAsync({
        page: 1,
        pageSize: 1,
        sortModel: {
          column: 'appointmentDate',
          direction: 'asc',
        },
      }),
    );
  };

  useEffect(() => {
    fetchTripLegs();
    if (enableReviewInvoicing) {
      dispatch(accountingSlice.fetchNeedsReviewAsync());
    }
  }, []);

  useEffect(() => {
    if (
      tripLegEventEmitted &&
      (tripLegEventEmitted.detailType === TripLegEventType.TripLegOpened ||
        tripLegEventEmitted.detailType === TripLegEventType.TripLegEscalated)
    ) {
      fetchTripLegs();
      dispatch(triplegSlice.setTripLegEventEmitted(null));
    }
  }, [tripLegEventEmitted]);

  const discussTrips = () => {
    navigate('/discussions');
  };

  const escalatedTrips = () => {
    navigate('/today');
  };

  const invoiceTrips = () => {
    navigate('/review-invoices');
  };

  const getTodaysDueTime = useMemo(() => {
    const tripLeg = todaysLegs?.items.length ? todaysLegs.items[0] : null;
    if (tripLeg) {
      const pickupDate = tripLeg.pickUpDueTimeUTC;
      const tz = tripLeg.pickUpAddressTimeZoneIANA;
      if (pickupDate && tz) {
        return dateUtils.formatAppointmentTime(pickupDate, tz);
      }
    }
    return '';
  }, [todaysLegs]);

  const getDiscussionDueDate = useMemo(() => {
    const tripLeg = discussionLegs?.items.length
      ? discussionLegs.items[0]
      : null;
    if (tripLeg) {
      const apptDate = tripLeg.appointmentDateTime || tripLeg.appointmentDate;
      if (apptDate) {
        return dateUtils.formatDate(
          DateTime.fromISO(apptDate).plus({ days: 7 }),
        );
      }
    }
    return '';
  }, [discussionLegs]);

  const getInvoiceDueDate = useMemo(() => {
    if (!needsReviewData) return '';

    let allLineItems: any[] = [];

    if (needsReviewData.groupedByProvider && needsReviewData.providers) {
      needsReviewData.providers.forEach((providerGroup) => {
        allLineItems.push(...providerGroup.lineItems);
      });
    } else if (needsReviewData.lineItems) {
      allLineItems = needsReviewData.lineItems;
    }

    if (allLineItems.length === 0) return '';

    const earliestDate = allLineItems.reduce(
      (earliest, item) => {
        const itemDate = DateTime.fromISO(item.createdOnUTC);
        return !earliest || itemDate < earliest ? itemDate : earliest;
      },
      null as DateTime | null,
    );

    return earliestDate ? dateUtils.formatDate(earliestDate) : '';
  }, [needsReviewData]);

  return (
    <StyledPaper className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.title}>
          {t('home.tasks')}
        </Typography>
      </Box>
      <Box className={classes.content}>
        <Box className={classes.section}>
          {loadingTodaysTrips && <CircularProgress color="primary" />}{' '}
          {!loadingTodaysTrips && (
            <>
              <ModeOfTravelOutlinedIcon
                className={classes.icon}
                fontSize="large"
              />
              <Typography variant="h3">{t('home.escalatedTrips')}</Typography>
              <Box component="div" className={classes.info}>
                <Typography variant="overline">{t('home.trips')}</Typography>
                <Typography variant="h3">{todaysLegs?.itemCount}</Typography>
              </Box>
              <Box component="div" className={classes.info}>
                {getTodaysDueTime && (
                  <>
                    <Typography variant="overline">
                      {t('home.nextPickup')}
                    </Typography>
                    <Typography variant="h3">{getTodaysDueTime}</Typography>
                  </>
                )}
              </Box>
              <IconButton className={classes.nav} onClick={escalatedTrips}>
                <ArrowForwardIosOutlinedIcon fontSize="large" />
              </IconButton>
            </>
          )}
        </Box>
        <Box className={classes.section}>
          {loadingDiscussionTrips && <CircularProgress color="primary" />}{' '}
          {!loadingDiscussionTrips && (
            <>
              <ChatBubbleOutlineOutlinedIcon
                className={classes.icon}
                fontSize="large"
              />
              <Typography variant="h3">{t('home.discussTrips')}</Typography>
              <Box component="div" className={classes.info}>
                <Typography variant="overline">{t('home.trips')}</Typography>
                <Typography variant="h3">
                  {discussionLegs?.itemCount}
                </Typography>
              </Box>
              <Box component="div" className={classes.info}>
                {getDiscussionDueDate && (
                  <>
                    <Typography variant="overline">{t('home.due')}</Typography>
                    <Typography variant="h3">{getDiscussionDueDate}</Typography>
                  </>
                )}
              </Box>
              <IconButton className={classes.nav} onClick={discussTrips}>
                <ArrowForwardIosOutlinedIcon fontSize="large" />
              </IconButton>
            </>
          )}
        </Box>
        {enableReviewInvoicing &&
          !loadingNeedsReview &&
          (needsReviewData?.totalLineItems ?? 0) > 0 && (
            <Box className={classes.section}>
              <CheckCircleOutlineRoundedIcon
                className={classes.icon}
                fontSize="large"
              />
              <Typography variant="h3">{t('home.reviewInvoicing')}</Typography>
              <Box component="div" className={classes.info}>
                <Typography variant="overline">{t('home.trips')}</Typography>
                <Typography variant="h3">
                  {needsReviewData?.totalLineItems || 0}
                </Typography>
              </Box>
              <Box component="div" className={classes.info}>
                {getInvoiceDueDate && (
                  <>
                    <Typography variant="overline">{t('home.due')}</Typography>
                    <Typography variant="h3">{getInvoiceDueDate}</Typography>
                  </>
                )}
              </Box>
              <IconButton className={classes.nav} onClick={invoiceTrips}>
                <ArrowForwardIosOutlinedIcon fontSize="large" />
              </IconButton>
            </Box>
          )}
      </Box>
    </StyledPaper>
  );
}
